import type { OrganisationRole } from '@evw/domain';
import { Button, Card } from '@evw/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { listCustomers, saveCustomer } from '../tenancy/api';

export function CustomersPage({
  organisationId,
  role
}: {
  organisationId: string;
  role: OrganisationRole;
}) {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const editable = ['owner', 'admin', 'manager', 'coordinator'].includes(role);
  const query = useQuery({
    queryKey: ['customers', organisationId],
    queryFn: () => listCustomers(organisationId)
  });
  const save = useMutation({
    mutationFn: () =>
      saveCustomer(organisationId, editingId, {
        given_name: givenName.trim(),
        family_name: familyName.trim(),
        email: email.trim(),
        phone: phone.trim()
      }),
    onSuccess: async () => {
      setEditingId(null);
      setGivenName('');
      setFamilyName('');
      setEmail('');
      setPhone('');
      await queryClient.invalidateQueries({ queryKey: ['customers', organisationId] });
    }
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate();
  }
  return (
    <div className="stack">
      <Card>
        <h2>Customers</h2>
        <p className="muted">Contact information needed for bookings and service operations.</p>
        {editable ? (
          <form className="inline-form" onSubmit={submit}>
            <label>
              First name
              <input
                required
                maxLength={80}
                value={givenName}
                onChange={(e) => setGivenName(e.target.value)}
              />
            </label>
            <label>
              Last name
              <input
                required
                maxLength={80}
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
              />
            </label>
            <label>
              Email
              <input
                type="email"
                maxLength={254}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label>
              Phone
              <input maxLength={40} value={phone} onChange={(e) => setPhone(e.target.value)} />
            </label>
            <Button disabled={save.isPending || (!email && !phone)}>
              {editingId ? 'Save customer' : 'Add customer'}
            </Button>
          </form>
        ) : null}
        {save.error ? <p className="error">{save.error.message}</p> : null}
      </Card>
      <Card>
        <div className="records">
          {query.data?.map((customer) => (
            <article className="record" key={customer.id}>
              <div>
                <strong>
                  {customer.given_name} {customer.family_name}
                </strong>
                <p>{customer.email ?? customer.phone}</p>
              </div>
              {editable ? (
                <Button
                  className="button-secondary"
                  onClick={() => {
                    setEditingId(customer.id);
                    setGivenName(customer.given_name);
                    setFamilyName(customer.family_name);
                    setEmail(customer.email ?? '');
                    setPhone(customer.phone ?? '');
                  }}
                >
                  Edit
                </Button>
              ) : null}
            </article>
          ))}
          {!query.data?.length ? <p className="muted">No customers yet.</p> : null}
        </div>
      </Card>
    </div>
  );
}
